"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  value: number;
  suffix: string;
  label: string;
};
const stats: Stat[] = [{
  value: 1200,
  suffix: "+",
  label: "Acres Protected"
}, {
  value: 45,
  suffix: "",
  label: "Bird Species Monitored"
}, {
  value: 12,
  suffix: "",
  label: "Community Workshops"
}, {
  value: 25,
  suffix: "",
  label: "Years of Advocacy"
}];
function CountUp({
  end,
  suffix,
  isVisible,
  delay
}: {
  end: number;
  suffix: string;
  isVisible: boolean;
  delay: number;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const duration = 1200;
    const startTime = performance.now() + delay;
    let raf = 0;
    const tick = (now: number) => {
      if (now < startTime) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isVisible, end, delay]);
  return <span>
      {count.toLocaleString()}
      {suffix}
    </span>;
}
export default function Impact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="3084" id="impact-by-the-numbers" className="relative bg-background text-foreground overflow-hidden py-32 px-4 md:px-8">
      {}
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.15] blur-[2px] bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722645/img/refuge_coastal_landscape.webp)]" aria-hidden="true" />

      {}
      <div className="absolute inset-0" style={{
      background: "linear-gradient(to right, hsl(160 61% 15% / 0.95), hsl(210 100% 50% / 0.92))"
    }} aria-hidden="true" />

      <div ref={ref} className="relative mx-auto max-w-7xl">
        {}
        <div className="mb-16 flex justify-center">
          <span className="font-default text-xs uppercase" style={{
          color: "var(--primary)",
          letterSpacing: "0.32em"
        }}>
            Our Impact
          </span>
        </div>

        {}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 md:gap-y-0">
          {stats.map((stat, i) => <div data-index={i} key={i} className={["relative flex flex-col items-center text-center px-4 md:px-6 py-2", i > 0 ? "md:border-l md:border-border" : "", i >= 2 ? "border-t border-border pt-12 md:border-t-0 md:pt-2" : "", i % 2 === 1 ? "border-l border-border" : "", i % 2 === 1 ? "md:border-l" : ""].join(" ")}>
              <div className="flex flex-col items-center">
                <div className="font-default font-light leading-none tracking-tight" style={{
              fontSize: "clamp(2.75rem, 8vw, 7rem)",
              color: "var(--foreground)"
            }}>
                  <CountUp end={stat.value} suffix={stat.suffix} isVisible={isVisible} delay={i * 300} />
                </div>

                {}
                <div className="mt-4 h-px transition-all ease-out" style={{
              backgroundColor: "var(--primary)",
              width: isVisible ? "3rem" : "0rem",
              transitionDuration: "700ms",
              transitionDelay: `${i * 300 + 600}ms`
            }} aria-hidden="true" />

                <div className="mt-5 font-default uppercase" style={{
              fontSize: "12px",
              letterSpacing: "0.18em",
              color: "var(--muted-foreground)"
            }}>
                  {stat.label}
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-24 flex flex-col items-center text-center transition-all ease-out" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(1rem)",
        transitionDuration: "900ms",
        transitionDelay: "1400ms"
      }}>
          <p className="font-default italic font-light max-w-3xl px-4" style={{
          color: "var(--primary)",
          fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
          lineHeight: "1.6"
        }}>
            &ldquo;Protecting our wildlife refuges is a commitment to the future of our natural heritage.&rdquo;
          </p>
          <div className="mt-6 font-default uppercase text-xs" style={{
          color: "var(--muted-foreground)",
          letterSpacing: "0.22em"
        }}>
            &mdash; Friends of the Refuges
          </div>
        </div>
      </div>
    </section>;
}